import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import dayjs from 'dayjs';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { ChatService } from '../../services/chat/chat.service';
import { NameService } from '../../services/name/name.service';
import { RolesService } from '../../services/roles/roles.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { UserService } from '../../services/user/user.service';
import { ChatMessage, ChatReaction } from '@sc/types';
import { ParticipantFsDolby } from '@sc/types';

@Component({
  selector: 'sc-chat-message',
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
})
export class ChatMessageComponent implements OnInit, OnDestroy {
  @Input() chatMessage: ChatMessage;
  @Input() chatGroupID: string;
  @Input() participants: Map<string, ParticipantFsDolby>;

  @Output() scrollToBottom = new EventEmitter();

  reactions$: Observable<ChatReaction[]>;
  reactions: ChatReaction[];
  reactionOptions: Array<string> = ['❤️', '👍', '👎', '👏', '😀', '😡'];

  name$: Observable<string>;

  isToday = false;

  subs: Subscription[] = [];

  constructor(
    public rolesService: RolesService,
    private chatService: ChatService,
    private userService: UserService,
    private sessionsService: SessionsService,
    private nameService: NameService,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit() {
    this.setupReactions();
    this.setupName();
    this.setupIsToday();
  }

  async setupReactions() {
    this.reactions$ = this.chatService.getChatReactions(
      this.sessionsService.studioSession$.value.sessionID,
      this.chatGroupID,
      this.chatMessage.chatMessageID
    );

    const sub = this.reactions$.subscribe(async (reactions) => {
      this.reactions = reactions;
    });
    this.subs.push(sub);

    // Scroll all the way to the bottom if the reactions load late.
    await firstValueFrom(this.reactions$);
    await new Promise((resolve) => setTimeout(resolve, 0));
    this.scrollToBottom.emit();
  }

  setupName() {
    this.name$ = this.nameService.getName(
      this.sessionsService.studioSession$.value.sessionID,
      this.userService.activeUser$.value.uid
    );
  }

  setupIsToday() {
    if (dayjs(this.chatMessage.datetime, 'x').isToday()) {
      this.isToday = true;
    }
  }

  /**
   * Toggles an emoji reaction on a given chat message
   *
   * @param emoji
   * @param chat
   */
  async toggleReaction(emoji: string, chat: ChatMessage) {
    const reactions = await firstValueFrom(this.reactions$);
    const existingReaction = reactions.filter((reaction) => reaction.emoji === emoji)[0];
    const name = await firstValueFrom(this.name$);
    if (existingReaction) {
      if (existingReaction.users.some((user) => user === name)) {
        // decrease if user reacted twice
        const users = existingReaction.users.filter((user) => user !== name);
        if (users.length) {
          this.chatService.updateChatReaction(
            this.sessionsService.studioSession$.value.sessionID,
            this.chatGroupID,
            chat.chatMessageID,
            existingReaction.chatReactionID,
            {
              emoji,
              users,
            }
          );
        } else {
          // delete if no users
          this.chatService.deleteChatReaction(
            this.sessionsService.studioSession$.value.sessionID,
            this.chatGroupID,
            chat.chatMessageID,
            existingReaction.chatReactionID
          );
        }
        this.analyticsService.track('removed chat reaction', { emoji });
      } else {
        // increase if user reacted once
        const users = existingReaction.users.concat([name]);
        this.chatService.updateChatReaction(
          this.sessionsService.studioSession$.value.sessionID,
          this.chatGroupID,
          chat.chatMessageID,
          existingReaction.chatReactionID,
          {
            emoji,
            users,
          }
        );
      }
    } else {
      // create if no users reacted
      const users = [name];
      this.chatService.createChatReaction(
        this.sessionsService.studioSession$.value.sessionID,
        this.chatGroupID,
        chat.chatMessageID,
        {
          emoji,
          users,
        }
      );
    }
    this.analyticsService.track('added chat reaction', { emoji });
  }

  /**
   * Prevents flickering/flashing in the list of chat reactions
   *
   * @param index - number
   * @param chatReaction - ChatReaction
   * @returns - string
   */
  trackByReaction(index: number, chatReaction: ChatReaction) {
    return chatReaction.emoji;
  }

  ngOnDestroy(): void {
    this.subs.forEach((item) => item.unsubscribe());
  }
}
